import type { PixelLogger } from '@/logger';

import { calculateBackoffDelay } from './types';
import type {
  IRetryEngine,
  RetryAttemptState,
  RetryConfig,
  RetryDebugState,
  RetryTask,
} from './types';

type InternalRetryEntry = {
  readonly id: string;
  task: RetryTask;
  attempt: number;
  status: RetryAttemptState['status'];
  nextRetryAt: number | null;
  timer: ReturnType<typeof setTimeout> | null;
  abort: () => void;
};

export class RetryEngine implements IRetryEngine {
  private readonly config: RetryConfig;

  private readonly logger: PixelLogger;

  private readonly entries = new Map<string, InternalRetryEntry>();

  private idCounter = 0;

  constructor(config: RetryConfig, logger: PixelLogger) {
    this.config = config;
    this.logger = logger;
  }

  async retry(task: RetryTask, taskId?: string): Promise<void> {
    const id = taskId ?? `retry-${++this.idCounter}`;

    return new Promise<void>((resolve, reject) => {
      let cancelled = false;

      const entry: InternalRetryEntry = {
        id,
        task,
        attempt: 0,
        status: 'pending',
        nextRetryAt: null,
        timer: null,
        abort: () => {
          cancelled = true;
          if (entry.timer) clearTimeout(entry.timer);
          entry.status = 'cancelled';
          entry.timer = null;
          this.entries.delete(id);
          resolve();
        },
      };

      this.entries.set(id, entry);

      const runAttempt = async (): Promise<void> => {
        if (cancelled) return;

        entry.attempt += 1;
        entry.status = 'running';
        entry.nextRetryAt = null;

        try {
          await task();
          entry.status = 'completed';
          this.entries.delete(id);
          resolve();
        } catch (error) {
          if (cancelled) return;

          if (entry.attempt >= this.config.maxAttempts) {
            entry.status = 'failed';
            this.entries.delete(id);
            reject(error);
            return;
          }

          const delay = calculateBackoffDelay(entry.attempt, this.config);
          entry.status = 'scheduled';
          entry.nextRetryAt = Date.now() + delay;

          this.logger.debug(
            `[RetryEngine] Attempt ${entry.attempt} failed, retry in ${delay}ms`
          );

          entry.timer = setTimeout(() => {
            entry.timer = null;
            void runAttempt();
          }, delay);
        }
      };

      void runAttempt();
    });
  }

  cancel(taskId: string): void {
    this.entries.get(taskId)?.abort();
  }

  cancelAll(): void {
    for (const entry of this.entries.values()) {
      entry.abort();
    }
    this.entries.clear();
  }

  getState(): RetryDebugState {
    return {
      pendingCount: this.entries.size,
      attempts: Array.from(this.entries.values()).map(entry => ({
        id: entry.id,
        attempt: entry.attempt,
        nextRetryAt: entry.nextRetryAt,
        status: entry.status,
      })),
    };
  }
}
